import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Trash, Plus, Eye, Mail } from "lucide-react";
import dayjs from "dayjs";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SchemaService,
  DynamicModuleService,
  DropdownService,
  PtsOnboardingMailService,
} from "@/services/APIs";
import {
  getSchemaProperties,
  mergeClearedSchemaFieldsForUpdate,
  prepareInitialValues,
  prepareSubmitValues,
} from "@/lib/dynamicSchemaFormValues";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import ProjectOnboardingStatusSection from "./ProjectOnboardingStatusSection";
import ProjectServiceConfirmationModal, {
  type ServiceConfirmationData,
} from "./ProjectServiceConfirmationModal";
import OnboardingMailConfirmModal from "./OnboardingMailConfirmModal";
import OnboardingMailSendingOverlay, {
  type OnboardingMailOverlayStatus,
} from "./OnboardingMailSendingOverlay";

const PROJECT_SCHEMA_NAME = "PTS Project";
const DESIGNATION_MASTER_SCHEMA_NAME = "PTS Designation Master";
const DESIGNATION_SCHEMA_NAME = "PTS Designation";
const THIRD_PARTY_CHARGES_SCHEMA_NAME = "PTS Third Party Charges";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const BILLING_TYPE_SCHEMA_NAME = "PTS Billing Type";
const CURRENCY_SCHEMA_NAME = "PTS Currency";

const PREVIEW_SIGNATORY_NAME = "Sandeep Singhvi";
const PREVIEW_SIGNATORY_DESIGNATION = "Director, Universal Software";
const PREVIEW_DATE_FORMAT = "DD MMM YYYY";

const ONBOARDING_MAIL_HOLD_AFTER_API_MS = 2000;
const ONBOARDING_MAIL_SUCCESS_DISPLAY_MS = 2000;

// Child table schema names (as used in x_child / API)
const PTS_PROJECT_MEMBERS_CHILD = "Pts_project_members_child";
const PTS_PROJECT_ESTIMATED_HOURS_CHILD = "Pts_project_estimated_hours_child";
const PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK =
  "pts_project_estimated_hours_child";
const PTS_PROJECT_EXPENSE_CHILD = "Pts_expense_child";

type ProjectEstimatedHoursRow = {
  index: string | number;
  designation?: string | number | null;
  estimateprice?: string | number | null;
  designation_label?: string;
  created_on?: string | null;
  updated_on?: string | null;
  [key: string]: unknown;
};

type ThirdPartyChargeRow = {
  index: string | number;
  id: string | number;
  item: string;
  description: string;
  estimated_yearly_cost: string | number | null;
  created_on?: string | null;
  updated_on?: string | null;
  [key: string]: unknown;
};

function getFirstPresentValue(
  record: Record<string, unknown>,
  keys: string[]
): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function buildTempIndex(prefix: string, value?: unknown): string {
  if (value !== undefined && value !== null && value !== "") {
    return `${prefix}_${String(value)}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function getIsoTimestamp(): string {
  return new Date().toISOString();
}

function normalizePayloadIndex(value: unknown, fallbackSeed: number): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return fallbackSeed;
}

function getDesignationKey(row: Record<string, unknown>): string | null {
  const value = getFirstPresentValue(row, [
    "designation",
    "designation_id",
    "pts_designation",
    "pts_designation_id",
    "designation_master",
    "designation_master_id",
    "id",
    "ID",
    "Id",
    "_id",
  ]);
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return String(value);
}

function getThirdPartyChargeId(row: Record<string, unknown>): string | null {
  const value = getFirstPresentValue(row, [
    "third_party_charge",
    "third_party_charge_id",
    "third_party_charges",
    "charge_id",
    "id",
    "ID",
    "Id",
    "_id",
    "value",
  ]);
  if (value === undefined || value === null || value === "") {
    return null;
  }
  if (typeof value === "object") {
    const nestedId = getFirstPresentValue(value as Record<string, unknown>, [
      "id",
      "ID",
      "Id",
      "_id",
      "value",
    ]);
    if (nestedId === undefined || nestedId === null || nestedId === "") {
      return null;
    }
    return String(nestedId);
  }
  return String(value);
}

function normalizeThirdPartyChargeRow(
  row: Record<string, unknown>
): ThirdPartyChargeRow | null {
  const chargeId = getThirdPartyChargeId(row);
  if (!chargeId) {
    return null;
  }

  const item = getFirstPresentValue(row, [
    "item",
    "item_name",
    "charge_item",
    "name",
    "title",
    "label",
    "display_name",
  ]);
  const description = getFirstPresentValue(row, [
    "description",
    "desc",
    "details",
    "remark",
    "remarks",
  ]);
  const estimatedYearlyCost = getFirstPresentValue(row, [
    "estimated_yearly_cost",
    "estimated_yearly_cost_inr",
    "yearly_cost",
    "estimated_cost",
    "cost",
    "amount",
  ]);
  const rowIndex =
    getFirstPresentValue(row, ["index"]) ??
    buildTempIndex("third_party_charge", chargeId);
  const createdOn = getFirstPresentValue(row, ["created_on"]);
  const updatedOn = getFirstPresentValue(row, ["updated_on"]);

  return {
    ...row,
    index:
      typeof rowIndex === "string" || typeof rowIndex === "number"
        ? rowIndex
        : buildTempIndex("third_party_charge", chargeId),
    id: chargeId,
    item: item !== undefined ? String(item) : String(chargeId),
    description: description !== undefined ? String(description) : "",
    estimated_yearly_cost:
      typeof estimatedYearlyCost === "string" ||
      typeof estimatedYearlyCost === "number" ||
      estimatedYearlyCost === null
        ? estimatedYearlyCost
        : estimatedYearlyCost !== undefined
          ? String(estimatedYearlyCost)
          : null,
    created_on: typeof createdOn === "string" ? createdOn : undefined,
    updated_on: typeof updatedOn === "string" ? updatedOn : undefined,
  };
}

function sanitizeThirdPartyChargeRows(rows: ThirdPartyChargeRow[]) {
  return rows.map((row, index) => {
    const fallbackIndex = Date.now() + index;
    const createdOn =
      typeof row.created_on === "string" && row.created_on.trim() !== ""
        ? row.created_on
        : getIsoTimestamp();
    const updatedOn = getIsoTimestamp();

    return {
      third_party_charges: row.id,
      index: normalizePayloadIndex(row.index, fallbackIndex),
      created_on: createdOn,
      updated_on: updatedOn,
    };
  });
}

/** Resolves a stored value to its schema enum label (Select/Radio fields). */
function getSchemaEnumLabel(
  schemaProperties: Record<string, any>,
  fieldName: string,
  value: unknown
): string | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const property = schemaProperties?.[fieldName];
  const enumOptions =
    property?.enum ?? property?.["x-component-props"]?.options;
  if (!Array.isArray(enumOptions)) {
    return null;
  }

  const match = enumOptions.find((option: any) => {
    if (option && typeof option === "object") {
      return String(option.value) === String(value);
    }
    return String(option) === String(value);
  });

  if (!match) {
    return null;
  }
  if (match && typeof match === "object") {
    return match.label !== undefined ? String(match.label) : null;
  }
  return String(match);
}

function formatPreviewDate(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "--";
  }
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.format(PREVIEW_DATE_FORMAT) : String(value);
}

function toDisplayText(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "--";
  }
  return String(value);
}

function formatEstimatedYearlyCost(value: string | number | null): string {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }
  return numericValue.toLocaleString("en-IN");
}

function normalizeExistingEstimatedHoursRow(
  row: Record<string, unknown>
): ProjectEstimatedHoursRow {
  const designation = getFirstPresentValue(row, [
    "designation",
    "designation_id",
    "pts_designation",
    "pts_designation_id",
    "designation_master",
    "designation_master_id",
  ]);
  const estimateprice = getFirstPresentValue(row, [
    "estimateprice",
    "estimate_price",
    "estimated_price",
    "price",
    "rate",
    "amount",
  ]);
  const designationLabel = getFirstPresentValue(row, [
    "designation_label",
    "designation_name",
    "designation_title",
    "label",
    "display_name",
    "name",
    "title",
  ]);
  const rowIndex =
    getFirstPresentValue(row, ["index", "id", "ID", "Id", "_id"]) ??
    designation;

  return {
    ...row,
    index: buildTempIndex("designation_rate", rowIndex),
    designation:
      designation !== undefined
        ? (designation as string | number | null)
        : (getDesignationKey(row) ?? null),
    estimateprice:
      estimateprice !== undefined
        ? (estimateprice as string | number | null)
        : "",
    designation_label:
      typeof designationLabel === "string" ? designationLabel : undefined,
  };
}

function normalizeDesignationMasterRow(
  row: Record<string, unknown>
): ProjectEstimatedHoursRow | null {
  const designationId = getFirstPresentValue(row, [
    "designation",
    "designation_id",
    "pts_designation",
    "pts_designation_id",
    "designation_master_id",
    "designation_master",
    "id",
    "ID",
    "Id",
    "_id",
  ]);

  if (
    designationId === undefined ||
    designationId === null ||
    designationId === ""
  ) {
    return null;
  }

  const designationLabel = getFirstPresentValue(row, [
    "designation_name",
    "designation_title",
    "designation_label",
    "label",
    "display_name",
    "name",
    "title",
  ]);
  const estimateprice = getFirstPresentValue(row, [
    "estimateprice",
    "estimate_price",
    "estimated_price",
    "price",
    "rate",
    "amount",
  ]);

  return {
    ...row,
    index: buildTempIndex("designation_rate", designationId),
    designation: designationId as string | number,
    estimateprice:
      estimateprice !== undefined ? (estimateprice as string | number) : "",
    designation_label:
      designationLabel !== undefined ? String(designationLabel) : undefined,
  };
}

function mergeDesignationRateRows(
  existingRows: ProjectEstimatedHoursRow[],
  designationMasterRows: ProjectEstimatedHoursRow[]
): ProjectEstimatedHoursRow[] {
  const existingMap = new Map<string, ProjectEstimatedHoursRow>();
  existingRows.forEach(row => {
    const key = getDesignationKey(row) ?? row.index;
    existingMap.set(String(key), row);
  });

  const mergedRows: ProjectEstimatedHoursRow[] = designationMasterRows.map(
    row => {
      const key = getDesignationKey(row) ?? row.index;
      const existing = existingMap.get(String(key));
      if (!existing) {
        return row;
      }
      existingMap.delete(String(key));
      const existingEstimatePrice = getFirstPresentValue(existing, [
        "estimateprice",
        "estimate_price",
        "estimated_price",
        "price",
        "rate",
        "amount",
      ]);
      const normalizedExistingEstimatePrice =
        typeof existingEstimatePrice === "string" ||
        typeof existingEstimatePrice === "number" ||
        existingEstimatePrice === null
          ? existingEstimatePrice
          : (row.estimateprice ?? "");

      return {
        ...row,
        ...existing,
        index: existing.index ?? row.index,
        designation: existing.designation ?? row.designation,
        estimateprice:
          existingEstimatePrice !== undefined
            ? normalizedExistingEstimatePrice
            : "",
        designation_label:
          existing.designation_label ?? row.designation_label ?? String(key),
      };
    }
  );

  return [...mergedRows, ...Array.from(existingMap.values())];
}

function sanitizeDesignationRateRows(rows: ProjectEstimatedHoursRow[]) {
  return rows.map((row, index) => {
    const numericValue =
      row.estimateprice === "" ||
      row.estimateprice === null ||
      row.estimateprice === undefined
        ? null
        : Number(row.estimateprice);
    const fallbackIndex = Date.now() + index;
    const createdOn =
      typeof row.created_on === "string" && row.created_on.trim() !== ""
        ? row.created_on
        : getIsoTimestamp();
    const updatedOn = getIsoTimestamp();

    return {
      designation: row.designation ?? null,
      estimateprice: Number.isFinite(numericValue) ? numericValue : null,
      index: normalizePayloadIndex(row.index, fallbackIndex),
      created_on: createdOn,
      updated_on: updatedOn,
    };
  });
}

function getDesignationDisplayName(
  row: ProjectEstimatedHoursRow,
  designationNameMap: Map<string, string>
): string {
  if (row.designation !== undefined && row.designation !== null) {
    const optionLabel = designationNameMap.get(String(row.designation));
    if (optionLabel) {
      return optionLabel;
    }
  }

  if (
    typeof row.designation_label === "string" &&
    row.designation_label.trim() !== ""
  ) {
    return row.designation_label;
  }

  if (
    row.designation !== undefined &&
    row.designation !== null &&
    row.designation !== ""
  ) {
    return String(row.designation);
  }

  return "--";
}

function stripEstimatedHoursChildFromSchema(schemaInput: any): {
  childFieldName: string;
  schemaWithoutEstimatedHoursChild: any;
} {
  if (!schemaInput) {
    return {
      childFieldName: PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK,
      schemaWithoutEstimatedHoursChild: schemaInput,
    };
  }

  const rawSchema = schemaInput.schema ?? schemaInput;
  const rawProperties = rawSchema?.properties;
  if (!rawProperties || typeof rawProperties !== "object") {
    return {
      childFieldName: PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK,
      schemaWithoutEstimatedHoursChild: schemaInput,
    };
  }

  const nextProperties = { ...rawProperties };
  let childFieldName = PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK;

  for (const [key, value] of Object.entries(rawProperties)) {
    const propertyValue = value as Record<string, unknown>;
    const componentProps =
      (propertyValue?.["x-component-props"] as Record<string, unknown>) ?? {};
    const xChild = componentProps.x_child;

    if (
      key === PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK ||
      xChild === PTS_PROJECT_ESTIMATED_HOURS_CHILD ||
      xChild === PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK
    ) {
      childFieldName = key;
      delete nextProperties[key];
      break;
    }
  }

  // Managed by the custom Third Party Charges card on this page
  if ("third_party_charges" in nextProperties) {
    delete nextProperties.third_party_charges;
  }

  if (schemaInput.schema !== undefined && schemaInput.schema !== null) {
    return {
      childFieldName,
      schemaWithoutEstimatedHoursChild: {
        ...schemaInput,
        schema: {
          ...rawSchema,
          properties: nextProperties,
        },
      },
    };
  }

  return {
    childFieldName,
    schemaWithoutEstimatedHoursChild: {
      ...rawSchema,
      properties: nextProperties,
    },
  };
}

/** Injects hideHeaderTitle and relationDisplaySchemas for Add Project child tables */
function injectProjectChildTableProps(schemaInput: any): any {
  if (!schemaInput) {
    return schemaInput;
  }
  const schema = schemaInput.schema ?? schemaInput;
  const propsRoot = schema?.properties;
  if (!propsRoot || typeof propsRoot !== "object") {
    return schemaInput;
  }

  const childTableConfig = {
    hideHeaderTitle: true,
    hideRowSelection: true,
    relationDisplaySchemas: {} as Record<string, string>,
  };
  const childConfig: Record<
    string,
    {
      hideHeaderTitle: boolean;
      hideRowSelection: boolean;
      relationDisplaySchemas: Record<string, string>;
    }
  > = {
    [PTS_PROJECT_MEMBERS_CHILD]: {
      ...childTableConfig,
      relationDisplaySchemas: { member: "PTS Emp" },
    },
    [PTS_PROJECT_ESTIMATED_HOURS_CHILD]: {
      ...childTableConfig,
      relationDisplaySchemas: { designation: "PTS Designation" },
    },
    pts_project_members_child: {
      ...childTableConfig,
      relationDisplaySchemas: { member: "PTS Emp" },
    },
    pts_project_estimated_hours_child: {
      ...childTableConfig,
      relationDisplaySchemas: { designation: "PTS Designation" },
    },
    [PTS_PROJECT_EXPENSE_CHILD]: {
      ...childTableConfig,
      relationDisplaySchemas: { employee: "PTS Emp" },
    },
    pts_expense_child: {
      ...childTableConfig,
      relationDisplaySchemas: { employee: "PTS Emp" },
    },
  };

  const nextProperties = { ...propsRoot };
  let changed = false;
  for (const [propKey, propVal] of Object.entries(propsRoot ?? {})) {
    const val = propVal as Record<string, unknown>;
    if (!val || typeof val !== "object") {
      continue;
    }
    const comp = val["x-component"];
    const compProps = (val["x-component-props"] ?? {}) as Record<
      string,
      unknown
    >;
    const xChild = compProps["x_child"] as string | undefined;
    const config = (xChild && childConfig[xChild]) ?? childConfig[propKey];
    if (comp === "CustomChildTable" && config) {
      nextProperties[propKey] = {
        ...val,
        "x-component-props": {
          ...compProps,
          hideHeaderTitle: config.hideHeaderTitle,
          hideRowSelection: config.hideRowSelection,
          relationDisplaySchemas: config.relationDisplaySchemas,
        },
      };
      changed = true;
    }
  }
  if (!changed) {
    return schemaInput;
  }
  const out =
    schemaInput.schema !== null
      ? { ...schemaInput, schema: { ...schema, properties: nextProperties } }
      : { ...schema, properties: nextProperties };
  return schemaInput.schema !== null ? out : out;
}

const AddProjectPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params?.id;
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDesignationToAdd, setSelectedDesignationToAdd] = useState("");
  const [designationRateRows, setDesignationRateRows] = useState<
    ProjectEstimatedHoursRow[]
  >([]);
  const [hasEditedDesignationRates, setHasEditedDesignationRates] =
    useState(false);
  const [selectedThirdPartyChargeToAdd, setSelectedThirdPartyChargeToAdd] =
    useState("");
  const [thirdPartyChargeRows, setThirdPartyChargeRows] = useState<
    ThirdPartyChargeRow[]
  >([]);
  const [hasEditedThirdPartyCharges, setHasEditedThirdPartyCharges] =
    useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewData, setPreviewData] =
    useState<ServiceConfirmationData | null>(null);
  const [isOnboardingMailConfirmOpen, setIsOnboardingMailConfirmOpen] =
    useState(false);
  const [onboardingMailOverlay, setOnboardingMailOverlay] =
    useState<OnboardingMailOverlayStatus | null>(null);
  const [onboardingMailSentTo, setOnboardingMailSentTo] = useState("");
  const [onboardingMailProjectInfo, setOnboardingMailProjectInfo] = useState({
    projectName: "--",
    clientName: "--",
    clientEmail: "--",
  });
  const form = useMemo(() => createForm({}), []);

  // Fetch the PTS Project schema
  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", PROJECT_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(PROJECT_SCHEMA_NAME),
    enabled: !!PROJECT_SCHEMA_NAME,
  });

  const { data: projectInfo, isLoading: projectLoading } = useQuery({
    queryKey: ["dynamic-model", PROJECT_SCHEMA_NAME, id],
    queryFn: () => {
      if (!id) {
        throw new Error("Project ID is required for edit mode");
      }
      return DynamicModuleService.getItemById(PROJECT_SCHEMA_NAME, id);
    },
    enabled: isEditMode && !!id,
  });

  const { data: designationMasterData, isLoading: designationMasterLoading } =
    useQuery({
      queryKey: ["dynamic-model", DESIGNATION_MASTER_SCHEMA_NAME, "all"],
      queryFn: async () => {
        const response = await DynamicModuleService.getItemsByParams(
          DESIGNATION_MASTER_SCHEMA_NAME,
          { page_size: 1000 }
        );
        return Array.isArray(response) ? response : [];
      },
      enabled: !!DESIGNATION_MASTER_SCHEMA_NAME,
    });

  const { data: designationOptions } = useQuery({
    queryKey: ["designation-dropdown", DESIGNATION_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DESIGNATION_SCHEMA_NAME),
    enabled: !!DESIGNATION_SCHEMA_NAME,
  });

  const { data: thirdPartyChargesData, isLoading: thirdPartyChargesLoading } =
    useQuery({
      queryKey: ["dynamic-model", THIRD_PARTY_CHARGES_SCHEMA_NAME, "all"],
      queryFn: async () => {
        const response = await DynamicModuleService.getItemsByParams(
          THIRD_PARTY_CHARGES_SCHEMA_NAME,
          { page_size: 1000 }
        );
        return Array.isArray(response) ? response : [];
      },
      enabled: !!THIRD_PARTY_CHARGES_SCHEMA_NAME,
    });

  const { data: thirdPartyChargeOptions } = useQuery({
    queryKey: ["third-party-charge-dropdown", THIRD_PARTY_CHARGES_SCHEMA_NAME],
    queryFn: () =>
      DropdownService.getDropdownData(THIRD_PARTY_CHARGES_SCHEMA_NAME),
    enabled: !!THIRD_PARTY_CHARGES_SCHEMA_NAME,
  });

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: billingTypeOptions } = useQuery({
    queryKey: ["billing-type-dropdown", BILLING_TYPE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(BILLING_TYPE_SCHEMA_NAME),
    enabled: !!BILLING_TYPE_SCHEMA_NAME,
  });

  const { data: currencyOptions } = useQuery({
    queryKey: ["currency-dropdown", CURRENCY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(CURRENCY_SCHEMA_NAME),
    enabled: !!CURRENCY_SCHEMA_NAME,
  });

  const { childFieldName, schemaWithoutEstimatedHoursChild } = useMemo(
    () => stripEstimatedHoursChildFromSchema(schemaInfo?.json ?? schemaInfo),
    [schemaInfo]
  );

  const normalizedDesignationMasterRows = useMemo(
    () =>
      (designationMasterData ?? [])
        .map(item =>
          normalizeDesignationMasterRow(item as Record<string, unknown>)
        )
        .filter((item): item is ProjectEstimatedHoursRow => item !== null),
    [designationMasterData]
  );

  const normalizedThirdPartyChargeOptions = useMemo(
    () =>
      (thirdPartyChargesData ?? [])
        .map(item =>
          normalizeThirdPartyChargeRow(item as Record<string, unknown>)
        )
        .filter((item): item is ThirdPartyChargeRow => item !== null),
    [thirdPartyChargesData]
  );

  // Set form initial values when in edit mode
  useEffect(() => {
    if (isEditMode && projectInfo) {
      const cleaned = removeMetaFields(projectInfo);
      form.setInitialValues(prepareInitialValues(cleaned));
      form.reset();
    }
  }, [isEditMode, projectInfo, form]);

  useEffect(() => {
    setHasEditedDesignationRates(false);
    setSelectedDesignationToAdd("");
    setHasEditedThirdPartyCharges(false);
    setSelectedThirdPartyChargeToAdd("");
  }, [id, childFieldName]);

  useEffect(() => {
    if (!schemaInfo || hasEditedDesignationRates) {
      return;
    }
    if (isEditMode && !projectInfo) {
      return;
    }
    if (!isEditMode && designationMasterLoading) {
      return;
    }

    const projectValues =
      isEditMode && projectInfo ? removeMetaFields(projectInfo) : {};
    const existingRawRows = Array.isArray(projectValues?.[childFieldName])
      ? (projectValues[childFieldName] as Record<string, unknown>[])
      : Array.isArray(
            projectValues?.[PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK]
          )
        ? (projectValues[PTS_PROJECT_ESTIMATED_HOURS_CHILD_FALLBACK] as Record<
            string,
            unknown
          >[])
        : [];

    const normalizedExistingRows = existingRawRows.map(
      normalizeExistingEstimatedHoursRow
    );
    const mergedRows = isEditMode
      ? normalizedExistingRows
      : normalizedDesignationMasterRows.length > 0
        ? mergeDesignationRateRows(
            normalizedExistingRows,
            normalizedDesignationMasterRows
          )
        : normalizedExistingRows;

    setDesignationRateRows(mergedRows);
  }, [
    childFieldName,
    designationMasterLoading,
    hasEditedDesignationRates,
    isEditMode,
    normalizedDesignationMasterRows,
    projectInfo,
    schemaInfo,
  ]);

  useEffect(() => {
    const formWithSetFieldValue = form as unknown as {
      setFieldValue?: (path: string, value: unknown) => void;
      setValues?: (values: Record<string, unknown>) => void;
    };
    const sanitizedRows = sanitizeDesignationRateRows(designationRateRows);

    if (typeof formWithSetFieldValue.setFieldValue === "function") {
      formWithSetFieldValue.setFieldValue(childFieldName, sanitizedRows);
      return;
    }

    if (typeof formWithSetFieldValue.setValues === "function") {
      formWithSetFieldValue.setValues({ [childFieldName]: sanitizedRows });
    }
  }, [childFieldName, designationRateRows, form]);

  useEffect(() => {
    if (hasEditedThirdPartyCharges) {
      return;
    }
    if (isEditMode && !projectInfo) {
      return;
    }
    if (thirdPartyChargesLoading) {
      return;
    }

    if (!isEditMode) {
      setThirdPartyChargeRows([]);
      return;
    }

    const projectValues = removeMetaFields(projectInfo);
    const existingRaw = projectValues?.third_party_charges;
    const existingRawRows = Array.isArray(existingRaw)
      ? (existingRaw as unknown[])
      : [];

    const optionMap = new Map(
      normalizedThirdPartyChargeOptions.map(row => [String(row.id), row])
    );

    const normalizedExistingRows = existingRawRows
      .map(item => {
        if (typeof item === "string" || typeof item === "number") {
          return optionMap.get(String(item)) ?? null;
        }
        if (item && typeof item === "object") {
          const normalized = normalizeThirdPartyChargeRow(
            item as Record<string, unknown>
          );
          if (!normalized) {
            return null;
          }
          const fromOptions = optionMap.get(String(normalized.id));
          return fromOptions
            ? {
                ...fromOptions,
                ...normalized,
                index: normalized.index,
                id: normalized.id,
                item: normalized.item || fromOptions.item,
                description: normalized.description || fromOptions.description,
                estimated_yearly_cost:
                  normalized.estimated_yearly_cost ??
                  fromOptions.estimated_yearly_cost,
                created_on:
                  normalized.created_on ?? fromOptions.created_on ?? undefined,
                updated_on:
                  normalized.updated_on ?? fromOptions.updated_on ?? undefined,
              }
            : normalized;
        }
        return null;
      })
      .filter((item): item is ThirdPartyChargeRow => item !== null);

    setThirdPartyChargeRows(normalizedExistingRows);
  }, [
    hasEditedThirdPartyCharges,
    isEditMode,
    normalizedThirdPartyChargeOptions,
    projectInfo,
    thirdPartyChargesLoading,
  ]);

  const designationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (designationOptions ?? []).forEach(
      (option: { value?: string; label?: string }) => {
        if (option?.value) {
          map.set(String(option.value), option.label ?? String(option.value));
        }
      }
    );
    return map;
  }, [designationOptions]);

  const thirdPartyChargeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (thirdPartyChargeOptions ?? []).forEach(
      (option: { value?: string; label?: string }) => {
        if (option?.value) {
          map.set(String(option.value), option.label ?? String(option.value));
        }
      }
    );
    return map;
  }, [thirdPartyChargeOptions]);

  const getThirdPartyChargeName = (row: ThirdPartyChargeRow): string => {
    const mapped = thirdPartyChargeNameMap.get(String(row.id));
    if (mapped) {
      return mapped;
    }
    return row.item || "--";
  };

  const missingDesignationMasterRows = useMemo(() => {
    const currentDesignationKeys = new Set(
      designationRateRows
        .map(row => getDesignationKey(row))
        .filter((key): key is string => !!key)
    );

    return normalizedDesignationMasterRows.filter(row => {
      const designationKey = getDesignationKey(row);
      return designationKey
        ? !currentDesignationKeys.has(designationKey)
        : false;
    });
  }, [designationRateRows, normalizedDesignationMasterRows]);

  useEffect(() => {
    if (!selectedDesignationToAdd) {
      return;
    }

    const isStillAvailable = missingDesignationMasterRows.some(row => {
      return getDesignationKey(row) === selectedDesignationToAdd;
    });

    if (!isStillAvailable) {
      setSelectedDesignationToAdd("");
    }
  }, [missingDesignationMasterRows, selectedDesignationToAdd]);

  const handleEstimatePriceChange = (
    rowIndex: string | number,
    nextValue: string
  ) => {
    setHasEditedDesignationRates(true);
    setDesignationRateRows(prev =>
      prev.map(row =>
        row.index === rowIndex ? { ...row, estimateprice: nextValue } : row
      )
    );
  };

  const handleDeleteDesignationRateRow = (rowIndex: string | number) => {
    setHasEditedDesignationRates(true);
    setDesignationRateRows(prev => prev.filter(row => row.index !== rowIndex));
  };

  const handleAddDesignationRateRow = () => {
    if (!selectedDesignationToAdd) {
      return;
    }

    const selectedRow = missingDesignationMasterRows.find(row => {
      return getDesignationKey(row) === selectedDesignationToAdd;
    });

    if (!selectedRow) {
      notify.error("Selected designation is not available to add");
      return;
    }

    setHasEditedDesignationRates(true);
    setDesignationRateRows(prev => [
      ...prev,
      {
        ...selectedRow,
        index: buildTempIndex("designation_rate", selectedDesignationToAdd),
      },
    ]);
    setSelectedDesignationToAdd("");
  };

  const missingThirdPartyChargeOptions = useMemo(() => {
    const selectedIds = new Set(
      thirdPartyChargeRows.map(row => String(row.id))
    );
    return normalizedThirdPartyChargeOptions.filter(
      row => !selectedIds.has(String(row.id))
    );
  }, [normalizedThirdPartyChargeOptions, thirdPartyChargeRows]);

  useEffect(() => {
    if (!selectedThirdPartyChargeToAdd) {
      return;
    }

    const isStillAvailable = missingThirdPartyChargeOptions.some(
      row => String(row.id) === selectedThirdPartyChargeToAdd
    );

    if (!isStillAvailable) {
      setSelectedThirdPartyChargeToAdd("");
    }
  }, [missingThirdPartyChargeOptions, selectedThirdPartyChargeToAdd]);

  const handleAddThirdPartyChargeRow = () => {
    if (!selectedThirdPartyChargeToAdd) {
      return;
    }

    const selectedRow = missingThirdPartyChargeOptions.find(
      row => String(row.id) === selectedThirdPartyChargeToAdd
    );

    if (!selectedRow) {
      notify.error("Selected third party charge is not available to add");
      return;
    }

    setHasEditedThirdPartyCharges(true);
    setThirdPartyChargeRows(prev => [
      ...prev,
      {
        ...selectedRow,
        index: buildTempIndex(
          "third_party_charge",
          selectedThirdPartyChargeToAdd
        ),
      },
    ]);
    setSelectedThirdPartyChargeToAdd("");
  };

  const handleDeleteThirdPartyChargeRow = (rowIndex: string | number) => {
    setHasEditedThirdPartyCharges(true);
    setThirdPartyChargeRows(prev => prev.filter(row => row.index !== rowIndex));
  };

  const handleSubmit = async () => {
    try {
      const formWithSetFieldValue = form as unknown as {
        setFieldValue?: (path: string, value: unknown) => void;
        setValues?: (values: Record<string, unknown>) => void;
      };
      const sanitizedRows = sanitizeDesignationRateRows(designationRateRows);
      if (typeof formWithSetFieldValue.setFieldValue === "function") {
        formWithSetFieldValue.setFieldValue(childFieldName, sanitizedRows);
      } else if (typeof formWithSetFieldValue.setValues === "function") {
        formWithSetFieldValue.setValues({ [childFieldName]: sanitizedRows });
      }

      await form.validate();
      const schemaProps = getSchemaProperties(schemaInfo?.json ?? schemaInfo);

      let modifiedValues = prepareSubmitValues(removeMetaFields(form.values));
      modifiedValues.third_party_charges =
        sanitizeThirdPartyChargeRows(thirdPartyChargeRows);

      if (isEditMode && projectInfo && Object.keys(schemaProps).length > 0) {
        const cleanedOriginal = removeMetaFields(projectInfo);
        modifiedValues = mergeClearedSchemaFieldsForUpdate(
          modifiedValues,
          cleanedOriginal,
          schemaProps
        );
        modifiedValues.third_party_charges =
          sanitizeThirdPartyChargeRows(thirdPartyChargeRows);
      }

      setIsSubmitting(true);

      if (isEditMode && id && projectInfo) {
        const originalForCompare = prepareSubmitValues(
          removeMetaFields(projectInfo)
        );
        if (!checkFormChanges(modifiedValues, originalForCompare, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          PROJECT_SCHEMA_NAME,
          id,
          modifiedValues
        );
        if (response) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ["dynamic-model", PROJECT_SCHEMA_NAME, id],
            }),
            queryClient.invalidateQueries({
              queryKey: ["moduleData", PROJECT_SCHEMA_NAME],
            }),
          ]);
          navigate(PTSNavigationRoutes.PROJECTS);
        }
      } else {
        const response = await DynamicModuleService.createItem(
          PROJECT_SCHEMA_NAME,
          modifiedValues
        );
        if (response) {
          await queryClient.invalidateQueries({
            queryKey: ["moduleData", PROJECT_SCHEMA_NAME],
          });
          navigate(PTSNavigationRoutes.PROJECTS);
        }
      }
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "create"} project`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(PTSNavigationRoutes.PROJECTS);
  };

  const buildDropdownNameMap = (
    options: { value?: string; label?: string }[] | undefined
  ) => {
    const map = new Map<string, string>();
    (options ?? []).forEach(option => {
      if (option?.value) {
        map.set(String(option.value), option.label ?? String(option.value));
      }
    });
    return map;
  };

  const employeeNameMap = useMemo(
    () => buildDropdownNameMap(employeeOptions),
    [employeeOptions]
  );

  const billingTypeNameMap = useMemo(
    () => buildDropdownNameMap(billingTypeOptions),
    [billingTypeOptions]
  );

  const currencyNameMap = useMemo(
    () => buildDropdownNameMap(currencyOptions),
    [currencyOptions]
  );

  /** Formily mutates `form.values` in place, so this is read on demand. */
  const readOnboardingMailProjectInfo = () => {
    const projectValues = removeMetaFields(form.values) as Record<
      string,
      unknown
    >;
    const pick = (keys: string[]) =>
      toDisplayText(getFirstPresentValue(projectValues, keys));

    return {
      projectName: pick(["projectname", "project_name", "name"]),
      clientName: pick(["clientname", "client_name", "client"]),
      clientEmail: pick([
        "client_email",
        "clientemail",
        "email",
        "client_mail",
      ]),
    };
  };

  const onboardingMailMutation = useMutation({
    mutationFn: (projectId: string) =>
      PtsOnboardingMailService.sendOnboardingMail(projectId),
    onSuccess: response => {
      setOnboardingMailSentTo(
        typeof response?.to === "string" ? response.to : ""
      );

      window.setTimeout(() => {
        setOnboardingMailOverlay("success");

        window.setTimeout(() => {
          setOnboardingMailOverlay(null);
          setOnboardingMailSentTo("");
        }, ONBOARDING_MAIL_SUCCESS_DISPLAY_MS);
      }, ONBOARDING_MAIL_HOLD_AFTER_API_MS);

      if (id) {
        queryClient.invalidateQueries({
          queryKey: ["pts-onboarding-status", id],
        });
      }
    },
    onError: (error: any) => {
      setOnboardingMailOverlay(null);
      notify.error(
        error?.message ?? "Failed to send onboarding mail. Please try again."
      );
    },
  });

  const handleOpenOnboardingMailConfirm = () => {
    if (!isEditMode || !id) {
      notify.warning(
        "Save the project first",
        "The onboarding mail can be sent once the project is saved."
      );
      return;
    }
    if (thirdPartyChargeRows.length === 0) {
      notify.warning(
        "Third Party Charges required",
        "Please add at least one third party charge before previewing."
      );
      return;
    }
    setOnboardingMailProjectInfo(readOnboardingMailProjectInfo());
    setIsOnboardingMailConfirmOpen(true);
  };

  const handleConfirmOnboardingMail = () => {
    if (!id) {
      return;
    }
    setIsOnboardingMailConfirmOpen(false);
    setOnboardingMailOverlay("sending");
    onboardingMailMutation.mutate(id);
  };

  const handleOpenPreview = async () => {
    if (thirdPartyChargeRows.length === 0) {
      notify.warning(
        "Third Party Charges required",
        "Please add at least one third party charge before previewing."
      );
      return;
    }

    try {
      setIsPreviewLoading(true);
      const projectValues = removeMetaFields(form.values) as Record<
        string,
        unknown
      >;
      const schemaProps = getSchemaProperties(schemaInfo?.json ?? schemaInfo);

      const resolveField = (keys: string[]) =>
        getFirstPresentValue(projectValues, keys);

      const resolveLabel = (keys: string[]) => {
        for (const key of keys) {
          const value = projectValues[key];
          if (value === undefined || value === null || value === "") {
            continue;
          }
          const label = getSchemaEnumLabel(schemaProps, key, value);
          return label ?? String(value);
        }
        return undefined;
      };

      const clientName = resolveField(["clientname", "client_name", "client"]);
      const companyName = resolveField([
        "company",
        "company_name",
        "companyname",
        "client_company",
        "clientcompany",
      ]);
      const projectName = resolveField(["projectname", "project_name", "name"]);

      const managerId = resolveField([
        "project_manager",
        "manager",
        "manager_id",
      ]);

      let managerName = managerId
        ? (employeeNameMap.get(String(managerId)) ?? "")
        : "";
      let managerEmail = "";
      let managerPhone = "";

      if (managerId) {
        try {
          const managerRecord = await DynamicModuleService.getItemById(
            EMPLOYEE_SCHEMA_NAME,
            String(managerId)
          );
          if (managerRecord) {
            const record = managerRecord as Record<string, unknown>;
            const firstName = getFirstPresentValue(record, [
              "firstname",
              "first_name",
              "fname",
            ]);
            const lastName = getFirstPresentValue(record, [
              "lastname",
              "last_name",
              "lname",
            ]);
            const fullName = [firstName, lastName]
              .filter(part => part !== undefined && part !== null)
              .map(part => String(part))
              .join(" ")
              .trim();
            if (fullName) {
              managerName = fullName;
            }
            const email = getFirstPresentValue(record, [
              "email",
              "emailaddress",
              "email_address",
              "official_email",
              "work_email",
            ]);
            const nestedUser =
              record.users && typeof record.users === "object"
                ? (record.users as Record<string, unknown>)
                : record.user && typeof record.user === "object"
                  ? (record.user as Record<string, unknown>)
                  : null;
            const phone =
              getFirstPresentValue(record, [
                "phonenumber",
                "phone_number",
                "mobile_number",
                "mobile_no",
                "mobileno",
                "mobile",
                "phone",
                "contact_no",
                "contact_number",
                "whatsapp",
                "whatsapp_number",
              ]) ??
              (nestedUser
                ? getFirstPresentValue(nestedUser, [
                    "phonenumber",
                    "phone_number",
                    "mobile_number",
                    "mobile_no",
                    "mobileno",
                    "mobile",
                    "phone",
                    "contact_no",
                    "contact_number",
                  ])
                : undefined);
            managerEmail =
              email !== undefined
                ? String(email)
                : nestedUser
                  ? String(
                      getFirstPresentValue(nestedUser, [
                        "email",
                        "emailaddress",
                        "email_address",
                      ]) ?? ""
                    )
                  : "";
            managerPhone = phone !== undefined ? String(phone) : "";
          }
        } catch {
          // Contact details are optional in the document
        }
      }

      const today = dayjs().format(PREVIEW_DATE_FORMAT);
      const preparedFor = toDisplayText(projectName);

      const resolveRelationName = (
        keys: string[],
        nameMap: Map<string, string>
      ) => {
        const value = resolveField(keys);
        if (value === undefined || value === null || value === "") {
          return undefined;
        }
        return nameMap.get(String(value)) ?? String(value);
      };

      setPreviewData({
        preparedFor,
        dateIssued: today,
        client: {
          name: toDisplayText(clientName),
          email: toDisplayText(
            resolveField([
              "client_email",
              "clientemail",
              "email",
              "client_mail",
            ])
          ),
          company: toDisplayText(companyName ?? clientName),
          phone: toDisplayText(
            resolveField([
              "client_mobile",
              "clientmobile",
              "client_phone",
              "mobile",
              "phone",
              "contact_no",
            ])
          ),
        },
        project: {
          name: toDisplayText(projectName),
          engagementType: toDisplayText(
            resolveLabel(["project_type1", "project_type", "type"])
          ),
          billingModel: toDisplayText(
            resolveRelationName(
              [
                "billing_model",
                "billingmodel",
                "billing_type",
                "billingtype",
                "billing_type1",
              ],
              billingTypeNameMap
            ) ?? resolveLabel(["billing_model", "billingmodel", "billing_type"])
          ),
          category: toDisplayText(
            resolveLabel(["project_category", "category"])
          ),
          startDate: formatPreviewDate(
            resolveField(["startdate", "start_date"])
          ),
          currency:
            resolveRelationName(["currency", "currency_id"], currencyNameMap) ??
            resolveLabel(["currency"]) ??
            "INR (₹)",
        },
        charges: thirdPartyChargeRows.map(row => ({
          item: getThirdPartyChargeName(row),
          description: row.description ?? "",
          estimatedYearlyCost: formatEstimatedYearlyCost(
            row.estimated_yearly_cost
          ),
        })),
        contact: {
          manager: toDisplayText(managerName),
          email: toDisplayText(managerEmail),
          phone: toDisplayText(managerPhone),
        },
        signatory: {
          name: PREVIEW_SIGNATORY_NAME,
          designation: PREVIEW_SIGNATORY_DESIGNATION,
          date: today,
        },
      });
      setIsPreviewOpen(true);
    } catch {
      notify.error("Failed to build project preview");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const projectSchemaJson = useMemo(
    () => injectProjectChildTableProps(schemaWithoutEstimatedHoursChild),
    [schemaWithoutEstimatedHoursChild]
  );

  if (
    schemaLoading ||
    (!isEditMode && designationMasterLoading) ||
    (isEditMode && projectLoading)
  ) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !projectInfo) {
    return <NotFound entityName="Project" />;
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">
              {isEditMode ? "Edit Project" : "Add New Project"}
            </h1>
            <p className="text-sm text-[#627384]">
              {isEditMode
                ? "Update the project information below"
                : "Fill in the project information below"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode && (
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenOnboardingMailConfirm}
              disabled={onboardingMailMutation.isPending}
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Onboarding Mail Sent
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleOpenPreview}
            disabled={isPreviewLoading}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            {isPreviewLoading ? "Preparing..." : "Preview"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
          >
            <Save className="h-4 w-4" />
            {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
          </Button>
        </div>
      </div>

      {previewData && (
        <ProjectServiceConfirmationModal
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          data={previewData}
        />
      )}

      <OnboardingMailConfirmModal
        open={isOnboardingMailConfirmOpen}
        projectName={onboardingMailProjectInfo.projectName}
        clientName={onboardingMailProjectInfo.clientName}
        clientEmail={onboardingMailProjectInfo.clientEmail}
        isSubmitting={onboardingMailMutation.isPending}
        onOpenChange={setIsOnboardingMailConfirmOpen}
        onConfirm={handleConfirmOnboardingMail}
      />

      <OnboardingMailSendingOverlay
        open={onboardingMailOverlay !== null}
        status={onboardingMailOverlay ?? "sending"}
        sentTo={onboardingMailSentTo}
      />

      {isEditMode && id && (
        <Card className="border border-gray-200 bg-white">
          <CardContent className="p-6">
            <ProjectOnboardingStatusSection projectId={id} />
          </CardContent>
        </Card>
      )}

      {/* Project Form */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-6">
          <SchemaFormView form={form} schemaJson={projectSchemaJson} />
        </CardContent>
      </Card>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-6">
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#111827]">
                Third Party Charges
              </h2>
            </div>
            <span className="text-sm text-[#627384]">
              {thirdPartyChargeRows.length} charge
              {thirdPartyChargeRows.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="w-full md:max-w-md">
              <Select
                value={selectedThirdPartyChargeToAdd}
                onValueChange={setSelectedThirdPartyChargeToAdd}
                disabled={
                  thirdPartyChargesLoading ||
                  missingThirdPartyChargeOptions.length === 0
                }
              >
                <SelectTrigger className="bg-white">
                  <SelectValue
                    placeholder={
                      thirdPartyChargesLoading
                        ? "Loading third party charges..."
                        : missingThirdPartyChargeOptions.length === 0
                          ? "All charges already added"
                          : "Select third party charge"
                    }
                  />
                </SelectTrigger>
                <SelectContent
                  sideOffset={6}
                  className="z-[100] border-gray-200 bg-white shadow-lg"
                >
                  {missingThirdPartyChargeOptions.map(row => (
                    <SelectItem key={String(row.id)} value={String(row.id)}>
                      {getThirdPartyChargeName(row)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleAddThirdPartyChargeRow}
              disabled={!selectedThirdPartyChargeToAdd}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 hover:bg-gray-100">
                  <TableHead className="border-r border-gray-200 font-semibold text-gray-800">
                    Item
                  </TableHead>
                  <TableHead className="border-r border-gray-200 font-semibold text-gray-800">
                    Description
                  </TableHead>
                  <TableHead className="border-r border-gray-200 font-semibold text-gray-800">
                    Estimated Yearly Cost (INR)
                  </TableHead>
                  <TableHead className="font-semibold text-gray-800">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {thirdPartyChargeRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-8 text-center text-sm text-gray-500"
                    >
                      No third party charges added.
                    </TableCell>
                  </TableRow>
                ) : (
                  thirdPartyChargeRows.map((row, index) => (
                    <TableRow
                      key={row.index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
                    >
                      <TableCell className="border-r border-gray-200 px-4 py-3 text-sm text-gray-800">
                        {getThirdPartyChargeName(row)}
                      </TableCell>
                      <TableCell className="border-r border-gray-200 px-4 py-3 text-sm text-gray-800">
                        {row.description || "--"}
                      </TableCell>
                      <TableCell className="border-r border-gray-200 px-4 py-3 text-sm text-gray-800">
                        {formatEstimatedYearlyCost(row.estimated_yearly_cost)}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteThirdPartyChargeRow(row.index)
                          }
                          className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
                        >
                          <Trash className="h-4 w-4" />
                          Delete
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-6">
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#111827]">
                Add Designation Rate
              </h2>
            </div>
            <span className="text-sm text-[#627384]">
              {designationRateRows.length} designation
              {designationRateRows.length === 1 ? "" : "s"}
            </span>
          </div>

          {isEditMode && (
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
              <div className="w-full md:max-w-md">
                <Select
                  value={selectedDesignationToAdd}
                  onValueChange={setSelectedDesignationToAdd}
                  disabled={
                    designationMasterLoading ||
                    missingDesignationMasterRows.length === 0
                  }
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue
                      placeholder={
                        designationMasterLoading
                          ? "Loading designations..."
                          : missingDesignationMasterRows.length === 0
                            ? "All designations already added"
                            : "Select designation to add"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent
                    sideOffset={6}
                    className="z-[100] border-gray-200 bg-white shadow-lg"
                  >
                    {missingDesignationMasterRows.map(row => {
                      const designationKey = getDesignationKey(row);
                      if (!designationKey) {
                        return null;
                      }

                      return (
                        <SelectItem key={designationKey} value={designationKey}>
                          {getDesignationDisplayName(row, designationNameMap)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleAddDesignationRateRow}
                disabled={!selectedDesignationToAdd}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Designation
              </Button>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 hover:bg-gray-100">
                  <TableHead className="border-r border-gray-200 font-semibold text-gray-800">
                    Designation
                  </TableHead>
                  <TableHead className="border-r border-gray-200 font-semibold text-gray-800">
                    Estimate Price
                  </TableHead>
                  <TableHead className="font-semibold text-gray-800">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {designationRateRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-8 text-center text-sm text-gray-500"
                    >
                      No designation data found.
                    </TableCell>
                  </TableRow>
                ) : (
                  designationRateRows.map((row, index) => (
                    <TableRow
                      key={row.index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
                    >
                      <TableCell className="border-r border-gray-200 px-4 py-3 text-sm text-gray-800">
                        {getDesignationDisplayName(row, designationNameMap)}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Input
                          type="number"
                          // step="0.01"
                          min="0"
                          value={row.estimateprice ?? ""}
                          onChange={event =>
                            handleEstimatePriceChange(
                              row.index,
                              event.target.value
                            )
                          }
                          placeholder="Enter estimate price"
                          className="max-w-[220px]"
                        />
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteDesignationRateRow(row.index)
                          }
                          className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
                        >
                          <Trash className="h-4 w-4" />
                          Delete
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddProjectPage;
